import {
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Coordinates } from '@dnd-kit/utilities';
import { useRef, useState } from 'react';
import { KeyboardDropTarget } from '../../../products/v2/utils/catalogDragAndDrop.types';
import { resolvePointerSide } from '../../../products/v2/utils/catalogDragAndDrop.utils';
import { createCatalogKeyboardCoordinateGetter } from '../../../products/v2/utils/catalogKeyboardCoordinates';
import {
  CatalogListActiveDragItem,
  CatalogListDragAndDrop,
  CatalogListDragItemData,
  CatalogListDropIndicatorState,
  UseCatalogListDragAndDropParams,
} from '../utils/catalogListDragAndDrop.types';
import { resolveCatalogListDropPosition } from '../utils/catalogListDragAndDrop.utils';

const MOUSE_ACTIVATION_DISTANCE_PX = 4;
const TOUCH_ACTIVATION_DELAY_MS = 200;
const TOUCH_ACTIVATION_TOLERANCE_PX = 5;

export const useCatalogListDragAndDrop = ({
  isEnabled,
  itemIds,
  onReorder,
}: UseCatalogListDragAndDropParams): CatalogListDragAndDrop => {
  const [activeItem, setActiveItem] = useState<CatalogListActiveDragItem | null>(null);
  const [dropIndicator, setDropIndicator] = useState<CatalogListDropIndicatorState | null>(null);
  const [isPersistingReorder, setIsPersistingReorder] = useState(false);

  const keyboardTargetRef = useRef<KeyboardDropTarget | null>(null);
  const pointerCoordinatesRef = useRef<Coordinates | null>(null);

  const trackPointerCollisionDetection: CollisionDetection = (args) => {
    pointerCoordinatesRef.current = args.pointerCoordinates;

    return closestCenter(args);
  };

  const keyboardCoordinateGetter = createCatalogKeyboardCoordinateGetter({
    getItemIds: () => itemIds,
    getTarget: () => keyboardTargetRef.current,
    onTargetChange: (target) => {
      keyboardTargetRef.current = target;
    },
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: MOUSE_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_ACTIVATION_DELAY_MS,
        tolerance: TOUCH_ACTIVATION_TOLERANCE_PX,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: keyboardCoordinateGetter }),
  );

  const resetDragState = () => {
    keyboardTargetRef.current = null;
    pointerCoordinatesRef.current = null;
    setActiveItem(null);
    setDropIndicator(null);
  };

  const resolveOver = ({ over }: Pick<DragOverEvent, 'over'>): KeyboardDropTarget | null => {
    const keyboardTarget = keyboardTargetRef.current;

    if (keyboardTarget) {
      return keyboardTarget;
    }

    if (!over) {
      return null;
    }

    return {
      overId: String(over.id),
      side: resolvePointerSide({
        pointerCoordinates: pointerCoordinatesRef.current,
        overRect: over.rect,
        isListMode: false,
      }),
    };
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as CatalogListDragItemData | undefined;

    setActiveItem({ id: String(event.active.id), imageUrl: data?.imageUrl });
  };

  const updateDropIndicator = ({ active, over }: Pick<DragOverEvent, 'active' | 'over'>) => {
    const resolvedOver = resolveOver({ over });

    if (!resolvedOver || resolvedOver.overId === active.id) {
      setDropIndicator(null);
      return;
    }

    const { overId, side } = resolvedOver;

    if (dropIndicator?.overId === overId && dropIndicator.side === side) {
      return;
    }

    const position = resolveCatalogListDropPosition({
      activeId: String(active.id),
      overId,
      side,
      itemIds,
    });

    if (position === null) {
      setDropIndicator(null);
      return;
    }

    setDropIndicator({ overId, side });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const resolvedOver = resolveOver({ over });
    resetDragState();

    if (!resolvedOver) {
      return;
    }

    const activeId = String(active.id);
    const position = resolveCatalogListDropPosition({
      activeId,
      overId: resolvedOver.overId,
      side: resolvedOver.side,
      itemIds,
    });

    if (position === null) {
      return;
    }

    setIsPersistingReorder(true);

    try {
      await onReorder({ catalogId: activeId, position });
    } finally {
      setIsPersistingReorder(false);
    }
  };

  return {
    isEnabled,
    isDraggingDisabled: !isEnabled || isPersistingReorder,
    activeItem,
    dropIndicator,
    dragAndDropContextProps: {
      sensors,
      collisionDetection: trackPointerCollisionDetection,
      onDragStart: handleDragStart,
      onDragMove: updateDropIndicator,
      onDragOver: updateDropIndicator,
      onDragEnd: handleDragEnd,
      onDragCancel: resetDragState,
    },
  };
};
